import Phaser from 'phaser';
import { BOSS } from '../config/GameConfig';

export interface BossProjectileOpts {
  /** 速度（像素/秒），默认 BOSS.projectileSpeed */
  speed?: number;
  /** 伤害，默认 BOSS.projectileDamage */
  damage?: number;
  /** 自定义着色（射手精英用橙红色） */
  tint?: number;
  /** 是否启用追击（false = 直线弹，射手精英用） */
  homing?: boolean;
  /** 视觉缩放（射手精英弹略小一点） */
  scale?: number;
}

/**
 * BossProjectile —— Boss / 射手精英 周期性向下扔的伤害包
 *
 * 行为：
 *   - 出生时朝下飞（speed，默认 BOSS.projectileSpeed）
 *   - 若 homing=true：每帧由 MainScene 调用 `homingUpdate(delta, target)` 缓慢转向玩家
 *     - 累计追击 BOSS.projectileHomingMaxTime ms 后变成直线弹（让玩家有窗口甩开）
 *   - homing=false：完全直线（射手精英弹，不追玩家）
 *   - 速度大小始终保持 _speed，靠 setVelocity 重新分解 x/y 分量
 */
export default class BossProjectile extends Phaser.GameObjects.Image {
  declare body: Phaser.Physics.Arcade.Body;
  damage: number;
  /** 是否启用追击；默认 true（保持 Boss 老行为） */
  homing: boolean;

  /** 速度模长（追击时重算分量用） */
  private _speed: number;
  /** 累计存活时间（ms），超过 BOSS.projectileHomingMaxTime 后停止追击 */
  private _aliveMs = 0;

  constructor(scene: Phaser.Scene, x: number, y: number, opts: BossProjectileOpts = {}) {
    super(scene, x, y, 'boss-bullet');
    scene.add.existing(this);
    scene.physics.add.existing(this);

    this._speed = opts.speed ?? BOSS.projectileSpeed;
    this.homing = opts.homing ?? true;

    this.body.setCircle(BOSS.projectileRadius);
    this.body.setVelocity(0, this._speed);
    this.body.setAllowGravity(false);

    this.damage = opts.damage ?? BOSS.projectileDamage;

    if (opts.tint !== undefined) this.setTint(opts.tint);
    if (opts.scale !== undefined) this.setScale(opts.scale);

    scene.tweens.add({
      targets: this,
      angle: 360,
      duration: 800,
      repeat: -1,
    });
  }

  /**
   * 朝目标缓慢转向；过了追击窗口就直线飞。
   * 若 homing=false（射手精英弹）直接返回不动。
   * @param delta 上一帧 ms
   * @param target 通常是玩家
   */
  homingUpdate(delta: number, target: { x: number; y: number }): void {
    if (!this.body) return;
    if (!this.homing) return;
    this._aliveMs += delta;
    if (this._aliveMs > BOSS.projectileHomingMaxTime) return;

    const speed = this._speed;
    const curAng = Math.atan2(this.body.velocity.y, this.body.velocity.x);
    const tgtAng = Math.atan2(target.y - this.y, target.x - this.x);
    let dAng = Phaser.Math.Angle.Wrap(tgtAng - curAng);
    const maxStep = (BOSS.projectileHomingTurnRate * delta) / 1000;
    dAng = Phaser.Math.Clamp(dAng, -maxStep, maxStep);
    const newAng = curAng + dAng;
    this.body.setVelocity(Math.cos(newAng) * speed, Math.sin(newAng) * speed);
  }
}
